import { Inject, inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { each, filter, groupBy, orderBy } from 'lodash';
import { select, Store } from '@ngrx/store';
import { Loader } from '@googlemaps/js-api-loader';
import { throwError } from 'rxjs/internal/observable/throwError';
import { Subject } from 'rxjs/internal/Subject';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { loadCrops } from '../_actions/crop';
import { AppState } from '../_reducers';
import { selectAllFields } from '../_selectors/field.selectors';
import { Crop, Field } from '../_models';
import { HttpParamsData } from '../_models/common/httpParamsData';
import { AuthenticationService } from './authentication.service';
import { CropMetricsService } from './cropMetrics.service';
import { MapService } from './map.service';
import { StreamChunk } from '../_models/bulkUpsert';
import { StaticMapProxyService } from './static-map-proxy.service';
import { Tag } from '../_models/tag';

@Injectable()
export class FieldService {
  // Services
  private http = inject(HttpClient);
  private cropMetricsService = inject(CropMetricsService);
  private store = inject(Store<AppState>);
  private mapsrv = inject(MapService);
  private translateService = inject(TranslateService);
  private authenticationService = inject(AuthenticationService);

  private apiBaseUrl = environment.apiBaseUrl;
  private telemetryServiceUrl = environment.telemetryServiceUrl;
  fieldList: Observable<Field[]>;
  allFields: Field[];
  fieldList$: BehaviorSubject<Array<Field>> = new BehaviorSubject<Array<Field>>([]);
  private googleMapsApiLoader: Loader;
  private authToken: string;
  private staticMapsProxyService = inject(StaticMapProxyService);

  constructor(
    @Inject('googleMapsApiKey') private apiKey: string,
    @Inject('googleMapsLibraries') private libraries: any,

    private zone: NgZone
  ) {
    this.fieldList = this.store.pipe(select(selectAllFields));

    // Initialize Google Maps API loader
    this.googleMapsApiLoader = new Loader({
      apiKey: this.apiKey,
      libraries: this.libraries,
    });

    // Aggiunta token di autenticazione
    const authToken = this.authenticationService.getAuthToken();
    if (authToken && this.authenticationService.isAuthTokenValide()) {
      this.authToken = authToken.access_token;
    }
  }

  getFromLocalStore(): Observable<Field[]> {
    return this.fieldList;
  }

  getFields(
    yearSelected: any,
    isMultiCompanies?: boolean,
    customParams?: HttpParamsData,
    yearSelectedCheck = true,
    thinAddCheck = true,
    storeData: boolean = true
  ): Observable<Field[]> {
    const excludesList = [
      'plantsPlantingDate',
      'mineralNitrogen',
      'listedInCAP',
      'country',
      'dateCreated',
      'lastUpdated',
      'plantsNote',
      'landRegistries',
      'fieldActivities',
      'city',
      'plantsX',
      'soilUrl',
      'granulometry',
      'limescale',
      'plantsY',
    ];
    let excludesAppend = '';
    excludesList.forEach((item) => {
      excludesAppend += '&excludes=' + item;
    });
    let currentYearSelected;
    if (yearSelected && yearSelected.payload) {
      currentYearSelected = yearSelected.payload;
    } else if (yearSelected && !yearSelected.payload) {
      currentYearSelected = yearSelected;
    } else {
      currentYearSelected = new Date().getFullYear();
    }
    let addYearSelected = '';
    if (yearSelectedCheck && currentYearSelected) {
      addYearSelected = '&year=' + currentYearSelected;
    }

    let thinCheck = '';
    if (thinAddCheck) {
      thinCheck = '&thinCrops=true';
    }
    return this.http
      .get<
        Field[]
      >(this.apiBaseUrl + 'api/private/v1/fields?' + thinCheck + excludesAppend + addYearSelected)
      .pipe(
        map((response) => {
          if (response) {
            if (storeData) {
              this.fieldList$.next(this.decodeGeoJsonFields(response));
              if (yearSelectedCheck) {
                this.store.dispatch(loadCrops({ fieldId: null, year: currentYearSelected }));
              }
              return this.getFieldFilteredByTags(response);
            } else return response;
          }
        })
      );
  }

  /**
   * Gets a list of fields for a specific cropYear; the Crops have only their ID
   * Despite the fields are not year related, the crops that are returned in the Field object are indeed cropYear related
   *
   * @param {number} year - The cropYear for which we want the list of fields
   * @returns {Observable<Field[]> } - Returns an Observable of Field List.
   *! NB this method does NOT update the store, differently from what the getFields method does
   */
  getFieldsWithThinCropsByYear(year: number): Observable<Field[]> {
    return this.http
      .get<Field[]>(this.apiBaseUrl + 'api/private/v1/fields?&thinCrops=false&year=' + year)
      .pipe(
        map((response) => {
          // Call the decodeGeoJsonFields method to create the StaticMapUrl
          return this.decodeGeoJsonFields(response);
        })
      );
  }

  getFieldList(): Field[] {
    this.fieldList.subscribe((data) => {
      this.allFields = data;
      return this.allFields;
    });

    return this.allFields;
  }

  getField(id: number): Observable<Field> {
    return this.http.get<Field>(this.apiBaseUrl + 'api/private/v1/fields/' + id).pipe(
      map((response) => {
        if (response) {
          return this.decodeGeoJsonField(response);
        }
      })
    );
  }

  getFieldById$(id: number): Observable<Field> {
    return this.getFromLocalStore().pipe(
      map((fields: Field[]) => fields.find((field) => field.id === id))
    );
  }

  getFieldById = (id: number): Field => {
    let field: Field;
    this.fieldList
      .pipe(first((fields: Field[]) => fields.length > 0))
      .subscribe((fields: Field[]) => {
        field = fields.find((f) => f.id === id);
      });
    return field;
  };

  getFieldByName = (name: string): Field => {
    let field: Field;
    this.fieldList
      .pipe(first((fields: Field[]) => fields.length > 0))
      .subscribe((fields: Field[]) => {
        field = fields.find((f) => f.name.toLowerCase().includes(name.toString().toLowerCase()));
      });
    return field;
  };

  saveFieldsBulk(fields): Observable<Field> {
    return this.http.put<Field>(this.apiBaseUrl + 'api/private/v1/fields/bulkUpdate', fields).pipe(
      map((response) => {
        if (response) {
          return response;
        }
      })
    );
  }

  /**
   * Sends the entire fields array to the bulk endpoint via HTTP POST,
   * then consumes the streaming response using a WritableStream.
   * Progress messages (and any received text) are emitted via the Observable.
   *
   * @param fields{Field[]} An array of field objects to send.
   * @returns An Observable<string> that emits progress updates.
   */
  upsertFieldsAsStream(fields: Field[]): Observable<StreamChunk> {
    const url = `${this.apiBaseUrl}api/private/v2/fields/bulkUpsert`;

    const chunkSubject = new Subject<StreamChunk>();

    let token = '';
    const getToken = this.authenticationService.getAuthToken();
    if (getToken && this.authenticationService.isAuthTokenValide()) {
      token = getToken.access_token;
    }

    // Use the Fetch API to perform the POST call.
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(fields),
    })
      .then((response) => {
        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const readChunk = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              // Process any remaining buffered text.
              this.processBuffer(buffer, chunkSubject);
              chunkSubject.complete();
              return;
            }
            // Append the decoded text.
            buffer += decoder.decode(value, { stream: true });

            // Split on newlines (each chunk expected to be a new line).
            const parts = buffer.split('\n');
            buffer = parts.pop() || '';

            parts.forEach((line) => {
              if (line.trim()) {
                // Each line is expected to start with "data:".
                const cleaned = line.replace(/^data:\s*/, '');
                try {
                  const chunk: StreamChunk = JSON.parse(cleaned);
                  // Use NgZone.run to ensure change detection updates the UI.
                  this.zone.run(() => chunkSubject.next(chunk));
                } catch (e) {
                  // Optionally, you could emit an error chunk here.
                  console.error('Error parsing chunk', e);
                }
              }
            });
            readChunk();
          });
        };
        readChunk();
      })
      .catch((error) => {
        // If the fetch fails, propagate the error.
        this.zone.run(() => chunkSubject.error(error));
      });

    return chunkSubject.asObservable();
  }
  private processBuffer(buffer: string, subject: Subject<StreamChunk>) {
    if (buffer.trim()) {
      try {
        const cleaned = buffer.replace(/^data:\s*/, '');
        const chunk: StreamChunk = JSON.parse(cleaned);
        subject.next(chunk);
      } catch (e) {
        console.error('Error processing final chunk', e);
      }
    }
  }

  saveField(fieldItem): Observable<Field> {
    const field = { ...fieldItem };
    if (field.farm) {
      const idfarm: any = {
        id: field.farm.id,
      };
      field.farm = idfarm;
    }

    delete field.lastUpdated;
    delete field.dateCreated;
    delete field.crops;
    delete field.activities;
    delete field.polygon;

    if (field.group) {
      if (field.group !== 'null') {
        const idgroup: any = {
          id: field.group.id,
        };
        field.group = idgroup;
      } else {
        field.group = null;
      }
    } else {
      field.group = null;
    }

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    field.companyId = companySelected ? companySelected.id : null;
    field.farm = companySelected ? companySelected.id : null;

    if (field.id) {
      return this.http
        .put<Field>(this.apiBaseUrl + 'api/private/v1/fields/' + field.id, field)
        .pipe(
          map((response) => {
            this.cropMetricsService.getCropMetrics().subscribe();
            return response;
          })
        );
    } else {
      return this.http.post<Field>(this.apiBaseUrl + 'api/private/v1/fields/', field).pipe(
        map((response) => {
          this.cropMetricsService.getCropMetrics().subscribe();
          return response;
        })
      );
    }
  }

  deleteField(id: number) {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/fields/' + id).pipe(
      map(() => {
        this.cropMetricsService.getCropMetrics().subscribe();
        return { id } as Field;
      })
    );
  }

  getFieldIdsByGroupId(group) {
    const ids = [];
    this.fieldList.subscribe((fields) => {
      fields.forEach((i) => {
        if (i.group && i.group.id == group) {
          ids.push(i.id);
        }
      });
    });
    return ids;
  }

  getFieldByCropId(cropId) {
    let fieldFounded: Field;
    this.fieldList
      .pipe(first((fields: Field[]) => fields.length > 0))
      .subscribe((fields: Field[]) => {
        fields.forEach((field) => {
          const crops = field.crops.map((crop) => crop.id);
          if (crops.includes(cropId)) {
            fieldFounded = field;
            return;
          }
        });
      });
    return fieldFounded;
  }

  getFieldsOPModule(farmsAppend, excludesAppend): Observable<Field[]> {
    return this.http
      .get<
        Field[]
      >(this.apiBaseUrl + 'api/private/v1/fields/' + farmsAppend + excludesAppend + '&thinCrops=true')
      .pipe(map((response) => response));
  }

  setGeoJsonAreaOnField = (field: any) => {
    const geoJsonTmp = this.getGeoJsonFeatureCollection();
    geoJsonTmp.features.push(this.getGeoJsonPolygonFeature());
    geoJsonTmp.features[0].geometry.coordinates = field.polygon.coordinates;
    field.geoJsonArea = JSON.stringify(geoJsonTmp);
    return field;
  };

  getFieldFilteredByTags(fields) {
    const storedTags = JSON.parse(localStorage.getItem('filterTags'));
    if (storedTags && storedTags.length > 0) {
      const fieldFoundedList = [];
      storedTags.forEach((tag) => {
        fields.forEach((f) => {
          if (f.tags && f.tags.length > 0 && f.tags.includes(tag.itemName)) {
            fieldFoundedList.push(f);
          }
        });
      });
      return fieldFoundedList;
    } else {
      return fields;
    }
  }

  filterFieldsByTags(fields: Field[], tags: Tag[]): Field[] {
    if (!tags?.length || fields?.length === 0) {
      return fields;
    }

    const tagNames = new Set(tags.map((t) => t.tagName));

    return fields.filter((f) => f.tags?.some((tag) => tagNames.has(tag)));
  }

  public getGeoJsonFeatureCollection() {
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  public getGeoJsonFeature() {
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [[]],
      },
    };
  }

  public getGeoJsonPolygonFeature() {
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [[]],
      },
    };
  }

  cleanCoordinatesOfAPolygon(geoJson) {
    let cleanPolygon = geoJson;
    if (
      geoJson &&
      geoJson.features[0].geometry &&
      geoJson.features[0].geometry.coordinates &&
      geoJson.features[0].geometry.coordinates.length > 0 &&
      geoJson.features[0].geometry.coordinates[0] &&
      geoJson.features[0].geometry.coordinates[0].length > 0
    ) {
      const newCoordList = [];
      geoJson.features[0].geometry.coordinates[0].forEach((coordinate) => {
        const newCoord = [];
        newCoord.push(coordinate[0]);
        newCoord.push(coordinate[1]);
        newCoordList.push(newCoord);
      });
      geoJson.features[0].geometry.coordinates[0] = newCoordList;
      cleanPolygon = geoJson;
    }
    return cleanPolygon;
  }

  public getGeoJsonLineFeature() {
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [],
      },
    };
  }

  private decodeGeoJsonFields(fields) {
    each(fields, (field) => {
      this.decodeGeoJsonField(field);
    });
    return fields;
  }

  decodeGeoJsonField(field) {
    const geoJsonTmp: any = this.getGeoJsonFeatureCollection();
    geoJsonTmp.features.push(this.getGeoJsonPolygonFeature());
    geoJsonTmp.features[0]['geometry'] = field.polygon;
    field.area = geoJsonTmp;
    field.staticMapUrl = this.staticMapsProxyService.getStaticMapUrl(field.area.features);
    return field;
  }

  getMultiYearCrops(field) {
    const tmpCropList = orderBy(field.crops, ['year'], ['desc']);

    let tmpSingleYearCrops = [];
    let tmpMultiYearCrops = [];
    const allCrops = [];

    tmpSingleYearCrops = filter(tmpCropList, (crop: Crop) => {
      return crop && (crop.multiYear == undefined || crop.multiYear == false);
    });

    tmpMultiYearCrops = filter(tmpCropList, (crop: Crop) => {
      return crop.multiYear == true;
    });

    const obj = groupBy(tmpMultiYearCrops, 'uuid');
    const ghost = Object.keys(obj).map((key) => obj[key]);

    ghost.forEach((cropsArray) => {
      if (cropsArray.length === 0) return;

      const orderedCrops = orderBy(cropsArray, ['year'], ['asc']);
      const earliestYear = orderedCrops[0].year;
      const latestYear = orderedCrops[orderedCrops.length - 1].year;

      let intervalMultiYear;
      if (orderedCrops.length === 1) {
        intervalMultiYear =
          this.translateService.instant('cobo.from') +
          ' ' +
          earliestYear.toString() +
          ' ' +
          this.translateService.instant('cobo.to') +
          ' ' +
          this.translateService.instant('diseases.zoneDetail.label_today');
      } else {
        intervalMultiYear =
          this.translateService.instant('cobo.from') +
          ' ' +
          earliestYear.toString() +
          ' ' +
          this.translateService.instant('cobo.to') +
          ' ' +
          latestYear.toString();
      }

      cropsArray[0] = { ...cropsArray[0], intervalMultiYear };
    });

    ghost.forEach((item) => {
      allCrops.push(item[0]);
    });

    field.allMultiYearCrops = allCrops;
    field.multiYearCrops = ghost;
    field.singleYearCrops = tmpSingleYearCrops;

    return field;
  }

  addIntervalMultiYear(cropsGroups: Array<Array<any>>) {
    cropsGroups.forEach((cropsArray) => {
      if (cropsArray.length === 0) return; // Salta se l'array è vuoto

      // Ordina le crop per anno
      const orderedCrops = cropsArray.sort((a, b) => a.year - b.year);

      // Determina l'intervallo basato sul numero di anni presenti
      let intervalMultiYear;
      const earliestYear = orderedCrops[0].year; // Anno più vecchio
      const latestYear = orderedCrops[orderedCrops.length - 1].year; // Anno più recente

      if (orderedCrops.length === 1) {
        // Solo una crop
        intervalMultiYear = `da ${earliestYear} fino ad oggi`;
      } else {
        // Più di una crop
        intervalMultiYear = `da ${earliestYear} a ${latestYear}`;
      }

      // Aggiunge intervalMultiYear solo al primo elemento del gruppo
      cropsArray[0] = { ...cropsArray[0], intervalMultiYear };
    });

    return cropsGroups;
  }

  sendToEquipment(e: any, fieldsCfg: SendFieldSelection[]) {
    if (e.provider == 'CLIMATEFIELDVIEW' || e.provider == 'JOHN_DEERE') {
      const url =
        this.telemetryServiceUrl +
        'api/private/v3/machine/exportFieldsToProviders?externalSource=' +
        e.provider;
      const payload = { fields: fieldsCfg };
      return this.http.post(url, payload).pipe(map((response) => response));
    }
    let url = this.apiBaseUrl + 'api/private/v3/isoxml/fields/send';
    const payload = { fields: fieldsCfg };

    const externalSource = e.provider;
    const externalId = e.selectedAgrirouterEndpointId;

    url += '?externalSource=' + externalSource + '&equipmentId=' + externalId;

    return this.http.post(url, payload).pipe(map((response) => response));
  }

  getFieldActivitiesKpi(year: number): Observable<any> {
    return this.http
      .get<any>(`${this.apiBaseUrl}api/private/v1/fieldActivitiesKpi?cropYear=${year}`)
      .pipe(
        map((response) => {
          // Verifica manuale dello status se necessario
          if (!response || (response.status && (response.status < 200 || response.status >= 500))) {
            throw new Error(`HTTP status ${response.status} returned`);
          }
          return response;
        }),
        catchError(this.handleError)
      );
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Errori lato client
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Errori lato server
      errorMessage = `Server-side error: ${error.status} ${error.message}`;
    }
    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  filterCropsByYearByFieldIdAndYear = (field: Field, year): Field => {
    if (field && field.crops && field.crops.length > 0) {
      const crops = [];
      field.crops.forEach((crop) => {
        if (crop.year == year) {
          crops.push(crop);
        }
      });
      field.crops = crops;
    }
    return field;
  };

  getCenterByFieldList = (fieldList: Array<Field>) => {
    const arrcenter = [];
    if (fieldList && fieldList.length > 0) {
      fieldList.forEach((field) => {
        const ccc = JSON.parse(field.geoJsonArea);
        const coords = ccc.features[0].geometry.coordinates[0];
        this.googleMapsApiLoader.load().then(() => {
          const bounds = new google.maps.LatLngBounds();
          for (let i = 0; i < coords.length; i++) {
            bounds.extend(new google.maps.LatLng(coords[i][0], coords[i][1]));
          }
          arrcenter[0] = bounds.getCenter().lat();
          arrcenter[1] = bounds.getCenter().lng();
        });
      });
    }
    return arrcenter;
  };

  getLatLngOfCenterField(field: Field): Promise<{ latitude: number; longitude: number }> {
    return new Promise((resolve, reject) => {
      const ccc = JSON.parse(field.geoJsonArea);
      const coords = ccc.features[0].geometry.coordinates[0];
      this.googleMapsApiLoader
        .load()
        .then(() => {
          const bounds = new google.maps.LatLngBounds();
          for (let i = 0; i < coords.length; i++) {
            bounds.extend(new google.maps.LatLng(coords[i][0], coords[i][1]));
          }
          const latitude = bounds.getCenter().lat();
          const longitude = bounds.getCenter().lng();
          resolve({ latitude, longitude });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}

interface SendFieldSelection {
  id: number;
  borders: boolean;
  obstacles: boolean;
  abLines: boolean;
}
